import { FC, useCallback, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { SavingPlansRecommendationsDataGridFooter } from "./SavingPlansRecommendationsDataGridFooter";
import { filterRecommendationsColumns } from "../../../utils/helpers/filterRecommendationsColumns";
import { CommitmentsAgGrid } from "../../../../common/components/CommitmentsAgGrid";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { connectedOrganizationExistsSelector } from "@/store/commitments/selectors/organisations/connectedOrganizationExistsSelector";
import { commitmentsRecommendationsSavingsPlanTypeSelector } from "@/store/commitments/selectors/recommendations/filters/commitmentsRecommendationsSavingsPlanTypeSelector";
import { commitmentsRecommendationsLoadingSelector } from "@/store/commitments/selectors/recommendations/data/commitmentsRecommendationsLoadingSelector";
import { getCommitmentsRecommendationsThunk } from "@/store/commitments/thunks/recommendations/getCommitmentsRecommendationsThunk";
import { commitmentsCurrentOrgIdSelector } from "@/store/commitments/selectors/common/commitmentsCurrentOrgIdSelector";
import { SavingsPlanType } from "@/services/cloudchipr.api";
import { SavingPlansRecommendationsOverviewAGGridToolbar } from "@/components/pages/commtiments/utilization-and-coverage/components/content/recommendations/data-grid/SavingPlansRecommendationsOverviewAGGridToolbar";
import { commitmentsRecommendationsOverviewGridDataSelector } from "@/store/commitments/selectors/recommendations/data/commitmentsRecommendationsOverviewGridDataSelector";

export const SavingPlansRecommendationsOverviewDataGrid: FC = () => {
  const dispatch = useAppDispatch();

  const data = useAppSelector(
    commitmentsRecommendationsOverviewGridDataSelector,
  );
  const orgExists = useAppSelector(connectedOrganizationExistsSelector);
  const orgId = useAppSelector(commitmentsCurrentOrgIdSelector);
  const loading = useAppSelector(commitmentsRecommendationsLoadingSelector);
  const savingsPlanTypes = useAppSelector(
    commitmentsRecommendationsSavingsPlanTypeSelector,
  );
  const empty = !loading && !data?.length;

  const columnDefs = useMemo(() => {
    return filterRecommendationsColumns(
      orgExists,
      savingsPlanTypes as SavingsPlanType[],
    );
  }, [orgExists, savingsPlanTypes]);

  const noRowOverlayComponent = useCallback(() => {
    if (empty) {
      return <SavingPlansRecommendationsDataGridFooter option="emptyData" />;
    }

    if (data?.length || loading) {
      return null;
    }

    return <SavingPlansRecommendationsDataGridFooter option="emptyData" />;
  }, [data?.length, loading, empty]);

  useEffect(() => {
    dispatch(getCommitmentsRecommendationsThunk());
  }, [dispatch, orgId]);

  return (
    <Box flex={1} border={1} borderColor="divider" borderRadius={2}>
      <CommitmentsAgGrid
        Toolbar={SavingPlansRecommendationsOverviewAGGridToolbar}
        loading={loading}
        columnDefs={columnDefs}
        data={data}
        localStorageUniqueKey="savingsPlanRecommendationsDataGrid"
        overrideWrapperStyles={savingsPlanRecommendationsGridWrapperStyles}
        noRowOverlayComponent={noRowOverlayComponent}
      />
    </Box>
  );
};

const savingsPlanRecommendationsGridWrapperStyles = {
  border: "none",
  borderRadius: 2,
  "& .ag-root-wrapper": {
    borderRadius: 2,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderTop: 1,
    borderColor: "grey.300",
    minHeight: "700px",
  },
};
